import { useState } from "react";
import { ArrowLeft, ArrowRight, Check, FolderOpen, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { StepPerson, personValid, PERSON_EMPTY } from "./onboarding/StepPerson";
import { StepProfile } from "./onboarding/StepProfile";
import { StepContract, contractValid } from "./onboarding/StepContract";
import { StepChecklist, checklistValid } from "./onboarding/StepChecklist";

const STEPS = ["Dane osobowe", "Forma i profil", "Umowa", "Komplet pierwszego dnia", "Gotowe"];
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

export const OnboardingWizard = ({ onClose, onDone }) => {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [person, setPerson] = useState({ existing: null, form: PERSON_EMPTY });
  const [profile, setProfile] = useState({ contract_type: "mandate", employment_type: "uz", is_student: false, is_foreigner: false, is_driver: true, rachunek_mode: "generate" });
  const [contract, setContract] = useState({ mode: "generate", sign_date: todayIso(), start_date: todayIso(), end_date: "", place: "Szczecin", hourly_rate: "", package_rate: "", multipack_extra_rate: "0.5" });
  const [checks, setChecks] = useState({});
  const [checkItems, setCheckItems] = useState(null);
  const [result, setResult] = useState(null);

  const personRate = person.existing?.hourly_rate ?? person.form?.hourly_rate ?? "";
  const canNext = [personValid(person), !!profile.contract_type, contractValid(contract, profile.contract_type, personRate), checklistValid(checkItems, checks)][step] ?? true;
  const plDocs = (n) => (n === 1 ? "1 dokument odhaczony" : (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) ? `${n} dokumenty odhaczone` : `${n} dokumentów odhaczonych`);

  const finish = async () => {
    setBusy(true);
    try {
      let emp = person.existing;
      if (!emp) {
        const f = person.form;
        const { data } = await api.post("/employees", { ...f, hourly_rate: f.hourly_rate === "" ? null : Number(f.hourly_rate) });
        emp = data;
        setPerson((p) => ({ ...p, existing: data }));
      }
      await api.put(`/hr/employees/${emp.id}/profile`, {
        employment_type: contract.mode === "generate" ? "auto" : profile.employment_type,
        is_student: profile.is_student, is_foreigner: profile.is_foreigner, is_driver: profile.is_driver, rachunek_mode: profile.rachunek_mode, notes: "",
      });
      let contractNo = null;
      if (contract.mode === "generate") {
        const payload = { contract_type: profile.contract_type, employee_id: emp.id, place: contract.place, sign_date: contract.sign_date, start_date: contract.start_date, end_date: contract.end_date || null };
        if (profile.contract_type === "mandate_per_package") { payload.package_rate = Number(contract.package_rate); payload.multipack_extra_rate = Number(contract.multipack_extra_rate || 0); }
        else payload.hourly_rate = Number(contract.hourly_rate || personRate || 0);
        const { data } = await api.post("/contracts", payload);
        contractNo = data.contract_number;
      }
      let saved = 0;
      const allowed = new Set((checkItems || []).map((i) => i.type));
      for (const [t, v] of Object.entries(checks)) {
        if (!v.done || !allowed.has(t)) continue;
        const fd = new FormData();
        fd.append("doc_type", t); fd.append("issued_at", v.issued_at || todayIso()); fd.append("valid_until", v.valid_until || ""); fd.append("note", "zebrano przy zatrudnieniu");
        if (v.file) fd.append("file", v.file);
        await api.post(`/hr/employees/${emp.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        saved += 1;
      }
      const file = (await api.get(`/hr/employees/${emp.id}`)).data;
      setResult({ emp, contractNo, saved, file });
      setStep(4);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nie udało się zakończyć onboardingu"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="onboarding-wizard" className="page-enter">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-label">// Kreator zatrudnienia</div>
          <h2 className="font-heading font-black text-xl md:text-2xl uppercase tracking-tight">Nowy pracownik</h2>
        </div>
        <button type="button" onClick={() => onClose()} data-testid="onb-close" aria-label="Zamknij" className="w-9 h-9 ring-1 ring-zinc-300 hover:ring-zinc-900 flex items-center justify-center"><X className="w-4 h-4" /></button>
      </div>
      <ol className="flex flex-wrap gap-2 mb-6" data-testid="onb-stepper">
        {STEPS.map((s, i) => (
          <li key={s} data-testid={`onb-step-indicator-${i}`} data-state={i === step ? "active" : i < step ? "done" : "todo"} className={`flex items-center gap-2 h-8 px-3 text-[11px] font-bold uppercase tracking-wider ring-1 ${i === step ? "bg-zinc-900 text-white ring-zinc-900" : i < step ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-white text-zinc-400 ring-zinc-200"}`}>
            <span className="font-mono">{i < step ? <Check className="w-3 h-3" /> : i + 1}</span>{s}
          </li>
        ))}
      </ol>

      {step === 0 && <StepPerson value={person} onChange={setPerson} />}
      {step === 1 && <StepProfile value={profile} onChange={setProfile} />}
      {step === 2 && <StepContract value={contract} onChange={setContract} contractType={profile.contract_type} hourlyRate={personRate} />}
      {step === 3 && <StepChecklist profile={profile} value={checks} onChange={setChecks} onItems={setCheckItems} />}
      {step === 4 && result && (
        <div data-testid="onb-summary" className="space-y-4">
          <div className="border border-emerald-300 bg-emerald-50 p-4">
            <div className="font-heading font-black uppercase tracking-tight text-emerald-800 flex items-center gap-2"><Check className="w-5 h-5" />Teczka założona: {result.emp.first_name} {result.emp.last_name}</div>
            <div className="text-sm text-emerald-900 mt-1">{result.contractNo ? `Umowa ${result.contractNo} wygenerowana · ` : "Umowa do uzupełnienia · "}{plDocs(result.saved)} · kompletność <b data-testid="onb-summary-health">{result.file.health ?? 0}%</b></div>
          </div>
          {result.file.todos.length > 0 && (
            <div>
              <div className="font-label mb-2">// Do zebrania w najbliższych dniach ({result.file.todos.length})</div>
              <ul className="space-y-1" data-testid="onb-summary-todos">{result.file.todos.map((t, i) => <li key={i} className="text-sm text-zinc-700 flex gap-2"><span className="text-zinc-300">—</span>{t.label}</li>)}</ul>
            </div>
          )}
          <button type="button" onClick={() => onClose(result.emp.id)} data-testid="onb-open-file" className="h-11 px-5 inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-heading font-black uppercase tracking-wider hover:bg-brand transition-colors"><FolderOpen className="w-4 h-4" />Otwórz teczkę</button>
        </div>
      )}

      {step < 4 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-200">
          <button type="button" onClick={() => setStep((s) => s - 1)} disabled={step === 0 || busy} data-testid="onb-back" className="h-10 px-4 inline-flex items-center gap-1.5 ring-1 ring-zinc-300 hover:ring-zinc-900 text-xs font-bold disabled:opacity-40"><ArrowLeft className="w-4 h-4" />Wstecz</button>
          {step < 3 ? (
            <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="onb-next" className="h-10 px-5 inline-flex items-center gap-1.5 bg-zinc-900 text-white text-xs font-heading font-black uppercase tracking-wider hover:bg-brand disabled:opacity-40 transition-colors">Dalej<ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button type="button" onClick={finish} disabled={!canNext || busy} data-testid="onb-finish" className="h-10 px-5 inline-flex items-center gap-1.5 bg-brand text-white text-xs font-heading font-black uppercase tracking-wider hover:bg-zinc-900 disabled:opacity-40 transition-colors">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Załóż teczkę
            </button>
          )}
        </div>
      )}
    </div>
  );
};
